const FALLBACKS = {
  primary: "#2F176F",
  secondary: "#5B21B6",
  accent: "#F2C94C",
  pageBg: "#FBF7EF",
  sectionBg: "#F8F1E6",
  surface: "#FFFFFF",
  surfaceAlt: "#FFF8EA",
  textPrimary: "#1F1720",
  textSecondary: "#475569",
  textMuted: "#64748B",
  textInverse: "#FFFFFF",
  footerBg: "#211044",
  footerText: "#F8FAFC",
  footerMuted: "#CBD5E1",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
};

function normalizeHex(value) {
  if (typeof value !== "string") return "";
  const raw = value.trim();
  const match = raw.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return "";

  const hex = match[1];
  if (hex.length === 3) {
    return `#${hex
      .split("")
      .map((char) => `${char}${char}`)
      .join("")}`.toUpperCase();
  }

  return `#${hex}`.toUpperCase();
}

function safeColor(value, fallback) {
  return normalizeHex(value) || fallback;
}

function hexToRgb(color) {
  const hex = normalizeHex(color);
  if (!hex) return null;
  const value = hex.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((value) =>
      Math.round(Math.max(0, Math.min(255, value)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`.toUpperCase();
}

export function withAlpha(color, alpha = 1) {
  const rgb = hexToRgb(color);
  const safeAlpha = Math.max(0, Math.min(1, Number(alpha)));
  if (!rgb) return `rgba(15,23,42,${safeAlpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${safeAlpha})`;
}

function mixColor(color, target, amount = 0.1) {
  const rgb = hexToRgb(color);
  const targetRgb = hexToRgb(target);
  if (!rgb || !targetRgb) return normalizeHex(color) || FALLBACKS.primary;
  const safeAmount = Math.max(0, Math.min(1, Number(amount)));
  return rgbToHex({
    r: rgb.r + (targetRgb.r - rgb.r) * safeAmount,
    g: rgb.g + (targetRgb.g - rgb.g) * safeAmount,
    b: rgb.b + (targetRgb.b - rgb.b) * safeAmount,
  });
}

export function darkenColor(color, amount = 0.12) {
  return mixColor(color, "#000000", amount);
}

export function lightenColor(color, amount = 0.12) {
  return mixColor(color, "#FFFFFF", amount);
}

function getRelativeLuminance(color) {
  const rgb = hexToRgb(color);
  if (!rgb) return 0;
  const channels = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
}

export function isDarkColor(color) {
  return getRelativeLuminance(color) < 0.46;
}

function getContrastRatio(colorA, colorB) {
  const lumA = getRelativeLuminance(colorA);
  const lumB = getRelativeLuminance(colorB);
  const light = Math.max(lumA, lumB);
  const dark = Math.min(lumA, lumB);
  return (light + 0.05) / (dark + 0.05);
}

export function getReadableTextColor(backgroundColor) {
  const background = safeColor(backgroundColor, FALLBACKS.primary);
  const darkText = FALLBACKS.textPrimary;
  const lightText = FALLBACKS.textInverse;
  return getContrastRatio(background, darkText) >= getContrastRatio(background, lightText)
    ? darkText
    : lightText;
}

export function generateOrgThemeTokens(branding = {}) {
  const primary = safeColor(branding.primaryColor, FALLBACKS.primary);
  const secondary = safeColor(branding.secondaryColor, FALLBACKS.secondary);
  const accent = safeColor(branding.accentColor, FALLBACKS.accent);
  const pageBg = safeColor(branding.backgroundColor, FALLBACKS.pageBg);
  const sectionBg = safeColor(branding.sectionBackgroundColor, FALLBACKS.sectionBg);
  const surface = safeColor(branding.surfaceColor, FALLBACKS.surface);
  const surfaceAlt = safeColor(branding.surfaceAltColor || branding.darkSurfaceColor, FALLBACKS.surfaceAlt);
  const textPrimary = safeColor(branding.textColor, getReadableTextColor(surface));
  const footerBg = safeColor(branding.footerBackground || branding.darkSurfaceColor, darkenColor(primary, 0.22));
  const footerText = safeColor(branding.footerText, getReadableTextColor(footerBg));
  const heroOverlay =
    typeof branding.heroOverlay === "string" && branding.heroOverlay.trim().startsWith("rgba")
      ? branding.heroOverlay.trim()
      : normalizeHex(branding.heroOverlay)
      ? withAlpha(branding.heroOverlay, isDarkColor(branding.heroOverlay) ? 0.58 : 0.36)
      : withAlpha(primary, isDarkColor(primary) ? 0.58 : 0.36);

  return {
    colors: {
      primary,
      secondary,
      accent,
      pageBg,
      sectionBg,
      surface,
      surfaceAlt,
      textPrimary,
      textSecondary: safeColor(branding.secondaryTextColor, FALLBACKS.textSecondary),
      textMuted: safeColor(branding.mutedTextColor, FALLBACKS.textMuted),
      textInverse: getReadableTextColor(primary),
      border: branding.borderColor || withAlpha(primary, 0.1),
      borderStrong: withAlpha(primary, 0.18),
      heroOverlay,
      footerBg,
      footerText,
      footerMuted: safeColor(branding.footerMuted, isDarkColor(footerBg) ? FALLBACKS.footerMuted : FALLBACKS.textSecondary),
      buttonPrimaryBg: primary,
      buttonPrimaryText: getReadableTextColor(primary),
      buttonPrimaryHover: darkenColor(primary, 0.12),
      buttonAccentBg: accent,
      buttonAccentText: getReadableTextColor(accent),
      buttonAccentHover: darkenColor(accent, 0.12),
      focusRing: withAlpha(accent, 0.34),
      success: FALLBACKS.success,
      warning: FALLBACKS.warning,
      danger: FALLBACKS.danger,
    },
  };
}

export function getOrgThemeStyle(tokens) {
  const colors = tokens?.colors || generateOrgThemeTokens().colors;
  return {
    "--org-primary": colors.primary,
    "--org-secondary": colors.secondary,
    "--org-accent": colors.accent,
    "--org-accent-soft": "#E8D9A8",
    "--org-eyebrow-color": "#8A5A12",
    "--org-eyebrow-on-dark": "#D5A147",
    "--org-footer-heading-color": "#E8D9A8",
    "--org-page-bg": colors.pageBg,
    "--org-section-bg": colors.sectionBg,
    "--org-surface": colors.surface,
    "--org-surface-alt": colors.surfaceAlt,
    "--org-text-primary": colors.textPrimary,
    "--org-text-secondary": colors.textSecondary,
    "--org-text-muted": colors.textMuted,
    "--org-text-inverse": colors.textInverse,
    "--org-border": colors.border,
    "--org-border-strong": colors.borderStrong,
    "--org-hero-overlay": colors.heroOverlay,
    "--org-footer-bg": colors.footerBg,
    "--org-footer-text": colors.footerText,
    "--org-footer-muted": colors.footerMuted,
    "--org-button-primary-bg": colors.buttonPrimaryBg,
    "--org-button-primary-text": colors.buttonPrimaryText,
    "--org-button-primary-hover": colors.buttonPrimaryHover,
    "--org-button-accent-bg": colors.buttonAccentBg,
    "--org-button-accent-text": colors.buttonAccentText,
    "--org-button-accent-hover": colors.buttonAccentHover,
    "--org-focus-ring": colors.focusRing,
    "--org-success": colors.success,
    "--org-warning": colors.warning,
    "--org-danger": colors.danger,
  };
}

export function applyOrgThemeToElement(element, tokens) {
  if (!element || !element.style) return;
  const style = getOrgThemeStyle(tokens);
  Object.entries(style).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
}
